import { LocalDateTime } from '@js-joda/core';
import { i18n } from '@lingui/core';
import { PourForm, PourMutation } from 'providers/api';
import { Weaken } from 'utils';
import {
  array,
  number,
  object,
  SchemaOf,
  string
} from 'yup';

export interface PourFormInput extends Omit<Weaken<PourForm>, 'plannedDate'> {
  plannedDate: LocalDateTime | null;
}

export type StopeFillPourFormInputs = Omit<Weaken<PourMutation>, 'data'> & {
  data: PourFormInput;
};

const generatePourSchema = (numberOfWarnings: number, minDate: LocalDateTime): SchemaOf<PourForm> => object().shape({
  reference: string().required(i18n._('A pour reference is required')),
  plannedDate: object()
    .shape({})
    .required(i18n._('A planned pour date is required'))
    .nullable()
    .test(
      'date-valid',
      i18n._('Pour date cannot be in the past'),
      (value: LocalDateTime) => value && (value.isEqual(minDate) || value.isAfter(minDate)),
    ),
  volume: number().typeError(i18n._('Volume must be a number')).required().moreThan(0, i18n._('Volume must be greater than zero')),
  volumeRetainedAfterOutflow: number().typeError(i18n._('Volume retained after outflow must be a number')).max(100).moreThan(0),
  pourPositionX: number().typeError(i18n._('Pour position X must be a number')),
  pourPositionY: number().typeError(i18n._('Pour position Y must be a number')),
  pourHeight: number()
    .typeError(i18n._('Pour height must be a number'))
    .moreThan(0, i18n._('Pour height must be greater than zero'))
    .required(i18n._('Pour height is required')),
  // Optional per-pour override of the paste beach slope. Blank means "use the project default", so this
  // must never be .required() - see resolvePourBeachAngle for the fallback chain.
  pasteBeachAngleDegrees: number()
    .typeError(i18n._('Beach angle must be a number'))
    .min(0, i18n._('Beach angle must be between 0 and 45 degrees'))
    .max(45, i18n._('Beach angle must be between 0 and 45 degrees')),
  acceptedWarnings: array()
    .min(numberOfWarnings, i18n._('You must accept all pour warnings before creating the'))
    .required(i18n._('You must accept all pour warnings before creating the')),
  // Optional; the stope-fill form never sets it, so the pour is planned without a flush.
  flushRecipeId: string(),
});

export const generatePourMutationSchema = (numberOfWarnings: number, minDate: LocalDateTime): SchemaOf<PourMutation> => object().shape({
  stopeId: string().required(i18n._('Stope Id is required')),
  recipeId: string().required(i18n._('Recipe Id is required')),
  recipeReference: string().required(i18n._('Recipe Reference is required')),
  data: generatePourSchema(numberOfWarnings, minDate),
});

export default generatePourSchema;
